import { useEffect, useRef, useState } from 'react';
import { Square, RotateCcw, X } from 'lucide-react';
import { useEditorPrefsStore, type FrameStyle, type WheelAction } from '../../stores/editorPrefsStore';
import { useCanvasStore } from '../../stores/canvasStore';

const STYLES: Array<{ value: FrameStyle; label: string }> = [
  { value: 'solid', label: 'Solid' },
  { value: 'dashed', label: 'Dashed' },
  { value: 'double', label: 'Double' },
];

const WHEEL_ACTIONS: Array<{ value: WheelAction; label: string }> = [
  { value: 'pan', label: 'Pan' },
  { value: 'zoom', label: 'Zoom' },
];

export function ArtboardSettingsPopover() {
  const { prefs, loaded, ensureLoaded, update, resetToDefaults } = useEditorPrefsStore();
  const { fabricCanvas } = useCanvasStore();
  const [open, setOpen] = useState(false);
  const popRef = useRef<HTMLDivElement | null>(null);
  const btnRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!loaded) void ensureLoaded();
  }, [loaded, ensureLoaded]);

  useEffect(() => {
    fabricCanvas?.requestRenderAll();
  }, [prefs, fabricCanvas]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (popRef.current?.contains(t)) return;
      if (btnRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="relative">
      <button
        ref={btnRef}
        onClick={() => setOpen((v) => !v)}
        title="Artboard frame"
        className={`
          w-8 h-8 flex items-center justify-center rounded-lg border transition-colors
          ${
            open
              ? 'bg-cyan-500/15 border-cyan-500/40 text-cyan-300'
              : 'bg-zinc-900/80 border-zinc-700 text-zinc-400 hover:text-zinc-100 hover:border-zinc-500'
          }
        `}
      >
        <Square size={15} />
      </button>

      {open && (
        <div
          ref={popRef}
          className="absolute top-10 right-0 w-72 bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl z-40 overflow-hidden"
        >
          <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-800">
            <div>
              <div className="text-xs font-semibold text-zinc-100">Artboard frame</div>
              <div className="text-[10px] text-zinc-500">Saved per user</div>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="p-1 rounded text-zinc-500 hover:text-zinc-200 hover:bg-zinc-800 transition-colors"
              aria-label="Close"
            >
              <X size={14} />
            </button>
          </div>

          <div className="p-3 space-y-3">
            <Row label="Show frame">
              <Toggle
                checked={prefs.artboard_frame_enabled}
                onChange={(v) => update({ artboard_frame_enabled: v })}
              />
            </Row>

            <Row label="Style">
              <div className="flex gap-1">
                {STYLES.map((s) => (
                  <button
                    key={s.value}
                    onClick={() => update({ artboard_frame_style: s.value })}
                    className={`
                      px-2 py-1 rounded text-[10px] uppercase tracking-wider transition-colors
                      ${
                        prefs.artboard_frame_style === s.value
                          ? 'bg-cyan-500/20 text-cyan-200 border border-cyan-500/40'
                          : 'bg-zinc-950 text-zinc-500 border border-zinc-800 hover:text-zinc-300'
                      }
                    `}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </Row>

            <Row label="Line color">
              <ColorInput
                value={prefs.artboard_stroke_color}
                onChange={(v) => update({ artboard_stroke_color: v })}
              />
            </Row>

            <Row label="Line width">
              <NumberInput
                value={prefs.artboard_stroke_width}
                min={0.5}
                max={8}
                step={0.5}
                onChange={(v) => update({ artboard_stroke_width: v })}
                suffix="px"
              />
            </Row>

            {prefs.artboard_frame_style === 'dashed' && (
              <Row label="Dash length">
                <NumberInput
                  value={prefs.artboard_dash_length}
                  min={2}
                  max={40}
                  step={1}
                  onChange={(v) => update({ artboard_dash_length: v })}
                  suffix="px"
                />
              </Row>
            )}

            <div className="pt-2 border-t border-zinc-800" />

            <Row label="Halo">
              <Toggle
                checked={prefs.artboard_halo_enabled}
                onChange={(v) => update({ artboard_halo_enabled: v })}
              />
            </Row>

            {prefs.artboard_halo_enabled && (
              <>
                <Row label="Halo color">
                  <ColorInput
                    value={prefs.artboard_halo_color}
                    onChange={(v) => update({ artboard_halo_color: v })}
                  />
                </Row>
                <Row label="Halo width">
                  <NumberInput
                    value={prefs.artboard_halo_width}
                    min={0.5}
                    max={6}
                    step={0.5}
                    onChange={(v) => update({ artboard_halo_width: v })}
                    suffix="px"
                  />
                </Row>
              </>
            )}

            <div className="pt-2 border-t border-zinc-800" />

            <div className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
              Navigation
            </div>

            <Row label="Scroll wheel">
              <div className="flex gap-1">
                {WHEEL_ACTIONS.map((a) => (
                  <button
                    key={a.value}
                    onClick={() => update({ nav_wheel_action: a.value })}
                    className={`
                      px-2 py-1 rounded text-[10px] uppercase tracking-wider transition-colors
                      ${
                        prefs.nav_wheel_action === a.value
                          ? 'bg-cyan-500/20 text-cyan-200 border border-cyan-500/40'
                          : 'bg-zinc-950 text-zinc-500 border border-zinc-800 hover:text-zinc-300'
                      }
                    `}
                  >
                    {a.label}
                  </button>
                ))}
              </div>
            </Row>

            <Row label="Invert scroll">
              <Toggle
                checked={prefs.nav_invert_scroll}
                onChange={(v) => update({ nav_invert_scroll: v })}
              />
            </Row>

            <Row label="Zoom speed">
              <NumberInput
                value={prefs.nav_zoom_speed}
                min={0.25}
                max={3}
                step={0.25}
                onChange={(v) => update({ nav_zoom_speed: v })}
              />
            </Row>

            <div className="text-[9px] leading-snug text-zinc-600 -mt-1">
              {prefs.nav_wheel_action === 'pan'
                ? 'Scroll to pan. Ctrl/Cmd+scroll or pinch to zoom. Shift+scroll for horizontal.'
                : 'Scroll to zoom around cursor. Hold Space or middle-click to drag.'}
            </div>

            <div className="pt-2 border-t border-zinc-800 flex justify-end">
              <button
                onClick={() => resetToDefaults()}
                className="flex items-center gap-1.5 px-2 py-1 rounded text-[11px] text-zinc-500 hover:text-zinc-100 hover:bg-zinc-800 transition-colors"
              >
                <RotateCcw size={11} /> Reset to defaults
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <label className="text-[11px] text-zinc-400">{label}</label>
      <div>{children}</div>
    </div>
  );
}

function Toggle({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      onClick={() => onChange(!checked)}
      className={`
        relative w-9 h-5 rounded-full transition-colors
        ${checked ? 'bg-cyan-500' : 'bg-zinc-700'}
      `}
    >
      <span
        className={`
          absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all
          ${checked ? 'left-4' : 'left-0.5'}
        `}
      />
    </button>
  );
}

function ColorInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const hex = value.length >= 7 ? value.slice(0, 7) : value;
  return (
    <div className="flex items-center gap-1.5">
      <input
        type="color"
        value={hex}
        onChange={(e) => {
          const alpha = value.length === 9 ? value.slice(7) : 'cc';
          onChange(`${e.target.value}${alpha}`);
        }}
        className="w-7 h-6 rounded border border-zinc-700 bg-zinc-950 cursor-pointer"
      />
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-20 bg-zinc-950 border border-zinc-700 rounded px-1.5 py-0.5 text-[10px] text-zinc-200 font-mono focus:outline-none focus:border-cyan-500/60"
      />
    </div>
  );
}

function NumberInput({
  value,
  onChange,
  min,
  max,
  step,
  suffix,
}: {
  value: number;
  onChange: (v: number) => void;
  min: number;
  max: number;
  step: number;
  suffix?: string;
}) {
  return (
    <div className="flex items-center gap-1">
      <input
        type="number"
        value={value}
        min={min}
        max={max}
        step={step}
        onChange={(e) => {
          const n = Number(e.target.value);
          if (Number.isFinite(n)) onChange(Math.max(min, Math.min(max, n)));
        }}
        className="w-14 bg-zinc-950 border border-zinc-700 rounded px-1.5 py-0.5 text-[10px] text-zinc-200 text-right focus:outline-none focus:border-cyan-500/60"
      />
      {suffix && <span className="text-[10px] text-zinc-600">{suffix}</span>}
    </div>
  );
}
